import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { UserLimits } from '@/components/user/UserLimits'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/utils/cn'
import {
    Activity,
    ChevronLeft,
    ChevronRight,
    LibraryBig,
    LogIn,
    LogOut,
    Plus,
    Settings as SettingsIcon,
    User
} from 'lucide-react'
import React from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'

const baseNavigation = [
  { name: 'Studies', href: '/studies', icon: Activity },
  { name: 'New Study', href: '/new-study', icon: Plus },
  { name: 'Library', href: '/results', icon: LibraryBig },
]

export function Layout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { aiewToken, logout, userInfo } = useAuth()
  const [isCollapsed, setIsCollapsed] = React.useState(false)

  // Admin controls are now in Settings page - no separate nav needed
  const navigation = baseNavigation

  const accountLabel =
    userInfo?.displayName ||
    userInfo?.name ||
    userInfo?.email ||
    "My Account"

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Skip to main content link for keyboard navigation */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md"
      >
        Skip to main content
      </a>

      {/* Sidebar */}
      <aside 
        className={cn(
          "bg-card border-r border-border flex flex-col relative transition-all duration-300 ease-in-out z-20",
          isCollapsed ? "w-20" : "w-20 lg:w-72"
        )}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Toggle Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-8 z-50 hidden h-6 w-6 rounded-full border border-border bg-background lg:flex items-center justify-center shadow-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronLeft className="h-3 w-3" />
          )}
        </button>

        {/* Background Elements - adjust blur/opacity based on collapse state for performance/visuals */}
        <div className="absolute top-0 left-0 w-full h-96 bg-blue-500/10 blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-full h-96 bg-purple-500/10 blur-[100px] pointer-events-none" />

        <div className="flex h-full flex-col relative z-10 overflow-hidden">
          {/* Logo */}
          <div className={cn(
            "flex h-16 items-center border-b bg-gradient-to-r from-card to-card/50 transition-all duration-300",
            isCollapsed ? "justify-center px-0" : "justify-center px-0 lg:justify-start lg:px-6"
          )}>
            <div className="flex items-center">
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={import.meta.env.VITE_AIEW_URL || "https://ai-edge.io"}
                      className="relative block flex-shrink-0 group/logo"
                      aria-label="Go to AI Edge website"
                    >
                      <img
                        src="/logo.png"
                        alt="AI Edge"
                        className={cn(
                          "object-contain drop-shadow-[0_0_8px_rgba(59,130,246,0.5)] transition-all duration-300 group-hover/logo:drop-shadow-[0_0_12px_rgba(59,130,246,0.7)]",
                          isCollapsed ? "h-10 w-10" : "h-12 w-12"
                        )}
                      />
                    </a>
                  </TooltipTrigger>
                  {isCollapsed && (
                    <TooltipContent side="right" sideOffset={10}>
                      Go to AI Edge
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              {!isCollapsed && (
                <button
                  type="button"
                  onClick={() => navigate('/studies')}
                  className="ml-3 hidden cursor-pointer lg:block"
                  aria-label="Go to Studies"
                >
                  <span className="text-xl font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent whitespace-nowrap opacity-100 transition-opacity duration-300 delay-100">
                    Optimizer
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-3 py-6">
            <ul role="list" className="space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href
                return (
                  <li key={item.name}>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link
                            to={item.href}
                            className={cn(
                              "group flex items-center justify-center px-2 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 relative",
                              isActive
                                ? "bg-blue-600/10 text-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.15)] border border-blue-500/20"
                                : "text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-transparent",
                              !isCollapsed && "lg:justify-start lg:px-3"
                            )}
                            aria-current={isActive ? "page" : undefined}
                            aria-label={item.name}
                          >
                            <item.icon
                              className={cn(
                                "h-6 w-6 mr-0 flex-shrink-0 transition-colors",
                                isActive
                                  ? "text-blue-400"
                                  : "text-muted-foreground group-hover:text-foreground",
                                !isCollapsed && "lg:h-5 lg:w-5 lg:mr-3"
                              )}
                              aria-hidden="true"
                            />
                            {!isCollapsed && (
                              <span className="hidden whitespace-nowrap opacity-100 transition-opacity duration-200 lg:inline">
                                {item.name}
                              </span>
                            )}
                            
                            {isActive && !isCollapsed && (
                              <div className="ml-auto hidden w-1.5 h-1.5 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(59,130,246,0.8)] lg:block" />
                            )}
                          </Link>
                        </TooltipTrigger>
                        {isCollapsed && (
                          <TooltipContent side="right" sideOffset={10}>
                            {item.name}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  </li>
                )
              })}
            </ul>
          </nav>

          {/* User Limits - User requested optimal UX, so hiding this complex card when collapsed is best */}
          <div className={cn(
            "hidden px-4 pb-4 transition-all duration-300 lg:block",
            isCollapsed ? "opacity-0 translate-y-10 pointer-events-none absolute bottom-20" : "opacity-100 translate-y-0 relative"
          )}>
             <UserLimits />
          </div>
          
          {/* User Menu */}
          <div className={cn(
            "border-t border-border bg-background/20 transition-all duration-300",
             isCollapsed ? "p-2" : "px-4 py-4"
          )}>
            {aiewToken ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className={cn(
                      "w-full h-auto hover:bg-white/5 rounded-xl border border-transparent hover:border-white/5 transition-all",
                      isCollapsed ? "justify-center py-2 px-0" : "justify-center py-2 px-0 lg:justify-start lg:py-3 lg:px-3"
                    )}
                  >
                    <div className={cn(
                      "rounded-full bg-gradient-to-tr from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold shadow-lg transition-all duration-300",
                      isCollapsed ? "h-10 w-10 mr-0" : "h-10 w-10 mr-0 lg:h-8 lg:w-8 lg:mr-3"
                    )}>
                      <User className={cn("transition-all", isCollapsed ? "h-5 w-5" : "h-5 w-5 lg:h-4 lg:w-4")} />
                    </div>
                    {!isCollapsed && (
                      <div className="hidden flex-col items-start overflow-hidden opacity-100 transition-opacity duration-300 lg:flex">
                        <span className="text-sm font-medium text-zinc-200 truncate w-full">{accountLabel}</span>
                         <span className="text-xs text-zinc-500 truncate w-full">Manage settings</span>
                      </div>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-60 bg-zinc-900 border-white/10 text-zinc-200" align="end" side="right" sideOffset={10}>
                  <DropdownMenuLabel>{accountLabel}</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={() => navigate('/settings')} className="focus:bg-white/10 focus:text-white">
                    <SettingsIcon className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={logout} className="focus:bg-red-500/10 focus:text-red-400 text-red-400">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="default"
                className={cn(
                  "w-full justify-center bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/20 transition-all",
                   isCollapsed ? "px-0" : ""
                )}
                onClick={logout}
              >
                <LogIn className={cn("h-4 w-4", isCollapsed ? "mr-0" : "mr-2")} />
                {!isCollapsed && "Log in"}
              </Button>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="min-w-0 flex-1 flex flex-col overflow-hidden">
        {/* Page content */}
        <main
          id="main-content"
          className="flex-1 overflow-y-auto overflow-x-auto bg-background px-3 py-5 sm:px-4"
          role="main"
          tabIndex={-1}
        >
          <Outlet />
        </main>
      </div>
    </div>
  )
}
